import { DataGrid } from '@mui/x-data-grid';
import { useState, useEffect, useRef, useMemo } from 'react';
import './styles.css';

export default function TableComponent({ rows, columns, pageSize = 5 }) {
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const mm = window.matchMedia('(orientation: landscape)');
    const update = () => {
      setIsLandscape(mm.matches);
      setIsPhone(window.innerWidth <= 768);
    };
    update();
    if (mm.addEventListener) mm.addEventListener('change', update);
    else mm.addListener(update);
    const onResize = () => update();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', update);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener('change', update);
      else mm.removeListener(update);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const isPhoneLandscape = isPhone && isLandscape;

  const totalColsWidth = useMemo(
    () => columns.reduce((sum, col) => sum + (col.width ?? 120), 0),
    [columns],
  );

  useEffect(() => {
    const checkScrollable = () => {
      if (!scrollContainerRef.current) return;
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollIndicator(scrollWidth > clientWidth);
    };
    const t = setTimeout(checkScrollable, 50);
    window.addEventListener('resize', checkScrollable);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', checkScrollable);
    };
  }, [rows, columns, totalColsWidth]);

  const enhancedColumns = columns.map(col => {
    if (col.field === 'performance') {
      return {
        ...col,
        renderCell: params => {
          const val = params.value;
          if (typeof val === 'number') {
            const ok = val >= 0;
            return (
              <span
                style={{ color: ok ? '#34c759' : '#ff3b30', fontWeight: 600 }}
              >
                {val.toFixed(2)}%
              </span>
            );
          }
          return val;
        },
      };
    }
    const isMonthField =
      ['oct07', 'nov07', 'dec07'].includes(col.field) ||
      /^(oct|nov|dec)/i.test(col.field);
    if (isMonthField) {
      return {
        ...col,
        renderCell: params => {
          const val = params.value;
          if (typeof val === 'number') {
            const ok = val >= 0;
            return (
              <span
                style={{ color: ok ? '#34c759' : '#ff3b30', fontWeight: 500 }}
              >
                {ok ? '+' : ''}
                {val.toFixed(2)}
              </span>
            );
          }
          return val;
        },
      };
    }
    return col;
  });

  // alturas/densidad
  const rowH = isPhoneLandscape ? 44 : 46;
  const headerH = isPhoneLandscape ? 44 : 46;
  const density = isPhoneLandscape ? 'compact' : 'standard';

  // ALTURA FIJA DEL GRID (clave para tener scroll vertical propio)
  const gridHeight = isPhoneLandscape ? 260 : 420; // ajusta a gusto

  return (
    <div
      className={`table-component ${
        isPhoneLandscape ? 'is-phone-landscape' : ''
      }`}
    >
      <div className="table-scroll-container" ref={scrollContainerRef}>
        <div
          className="grid-height-wrap"
          style={{
            minWidth: `${Math.max(totalColsWidth, 600)}px`,
            height: `${gridHeight}px`,
          }}
        >
          <DataGrid
            rows={rows}
            columns={enhancedColumns}
            hideFooter
            disableSelectionOnClick
            disableColumnMenu
            density={density}
            rowHeight={rowH}
            columnHeaderHeight={headerH}
            sx={{
              border: 'none',
              width: Math.max(totalColsWidth, 600),
              minWidth: Math.max(totalColsWidth, 600),
              height: gridHeight,

              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e9ecef',
              },
              '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
                lineHeight: isPhoneLandscape ? '1.1rem' : '1.25rem',
                fontSize: isPhoneLandscape ? '12px' : '14px',
                paddingTop: isPhoneLandscape ? '4px' : '8px',
                paddingBottom: isPhoneLandscape ? '4px' : '8px',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#f8f9fa' },

              '& .MuiDataGrid-main': { overflowX: 'hidden' },
              '& .MuiDataGrid-scrollbar--horizontal': { display: 'none' },
              '& .MuiDataGrid-scrollbarFiller': { display: 'none' },
              '& .MuiDataGrid-scrollbarFiller--header': { display: 'none' },

              // scroll vertical solo en virtualScroller
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
                overflowY: 'auto',
              },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                width: 12,
              },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                backgroundColor: '#eef3f6',
                borderRadius: 8,
              },
              '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(180deg,#007aff,#5ac8fa)',
                borderRadius: 10,
                border: '1px solid rgba(0,122,255,.35)',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
